export type AnalyticsMetricRow = { section: string; dimensions: Record<string, unknown>; metrics: Record<string, number> };
export function voiceConnectionNotice(rows: AnalyticsMetricRow[]) {
  let total = 0, failures = 0;
  for (const row of rows) {
    if (row.section !== 'voiceAttempts') continue;
    total += row.metrics.attempts || 0;
    if (['failure', 'timeout'].includes(String(row.dimensions.outcome))) failures += row.metrics.attempts || 0;
  }
  // A transparent initial inspection threshold, not a learned anomaly baseline.
  return { total, failures, inspect: total >= 30 && failures >= 5 && failures / total >= 0.2 };
}
export function idiomSummary(row: AnalyticsMetricRow) {
  const m = row.metrics;
  const histogram = Object.entries(m).filter(([key]) => /^wordHistogram:\d+$/.test(key))
    .map(([key, count]) => ({ value: Number(key.slice('wordHistogram:'.length)), count })).sort((a, b) => a.value - b.value);
  const count = histogram.reduce((sum, item) => sum + item.count, 0);
  const at = (rank: number) => { let passed = 0; for (const item of histogram) { passed += item.count; if (passed > rank) return item.value; } return 0; };
  return { samples: m.wordSamples || 0, mean: m.wordSamples ? m.successfulWords / m.wordSamples : null,
    median: count ? (at(Math.floor((count - 1) / 2)) + at(Math.floor(count / 2))) / 2 : null };
}
export function metricRatios(row: AnalyticsMetricRow, allRows: AnalyticsMetricRow[] = []) {
  const m = row.metrics;
  const results: { label: string; numerator: number; denominator: number; value: number | null; duration: boolean }[] = [];
  const add = (label: string, numerator: string, denominator: string, duration = false) => {
    const n = m[numerator] || 0, d = m[denominator] || 0;
    results.push({ label, numerator: n, denominator: d, value: d > 0 ? n / d : null, duration });
  };
  if (row.section === 'sort') add('完成率', 'completed', 'started');
  if (row.section === 'games') {
    add('正常完成率', 'completed', 'started');
    const base = allRows.filter(item => item.section === 'games' && item.dimensions.game === row.dimensions.game).reduce((sum, item) => sum + (item.metrics.started || 0), 0);
    results.push({ label: '同游戏配置使用率', numerator: m.started || 0, denominator: base, value: base ? (m.started || 0) / base : null, duration: false });
    if (row.dimensions.game === 'werewolf-silly') {
      add('村民胜率', 'wins:village', 'decided'); add('狼人胜率', 'wins:werewolf', 'decided');
    }
  }
  if (['voice', 'voiceByGame'].includes(row.section)) {
    add('开麦时长占比', 'microphoneMs', 'eligibleMs');
    add('开麦参与比例', 'microphoneParticipants', 'eligibleParticipants');
  }
  if (row.section === 'operations') add('已返回操作成功率', 'success', 'resolved');
  if (row.section === 'packs') {
    const selectedBase = allRows.filter(item => item.section === 'packSelectionBase').reduce((sum, item) => sum + (item.metrics.games || 0), 0);
    const issuedBase = allRows.filter(item => item.section === 'packs').reduce((sum, item) => sum + (item.metrics.issued || 0), 0);
    for (const [label, numerator, denominator] of [
      ['选库局数占比', m.selectedGames || 0, selectedBase], ['官方出题占比', m.issued || 0, issuedBase],
    ] as const) results.push({ label, numerator, denominator, value: denominator ? numerator / denominator : null, duration: false });
  }
  if (row.section === 'words') {
    if (row.dimensions.mode === 'classic') {
      add('猜中比例', 'correct', 'eligible');
      add('无人猜中率', 'unguessed', 'completed');
      add('主动跳过率', 'skipped', 'issued');
      add('首次猜中平均耗时', 'firstCorrectMs', 'firstCorrectSamples', true);
    }
    if (row.dimensions.mode === 'relay') {
      add('空白节点比例', 'blanks', 'nodes'); add('超时节点比例', 'timeouts', 'nodes');
      add('首猜保留原词比例', 'firstRetained', 'firstRetainedSamples');
      add('末猜保留原词比例', 'finalRetained', 'finalRetainedSamples');
      add('实际投票赞成比例', 'positiveVotes', 'votes');
    }
  }
  return results;
}
